import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { User, Envelope, Lock, GoogleLogo, ArrowRight, Check } from '@phosphor-icons/react';

// Logo component
const FinanciALLogo = () => (
  <img 
    src="https://customer-assets-0z36b82j.emergentagent.net/job_1ce9899f-22e9-4951-9d4b-a91c40afd391/artifacts/ktwxigwh_Liquid%20Glass%20Logo.png.png" 
    alt="FinanciAL" 
    className="w-10 h-10 rounded-xl object-cover"
  />
);

const Register = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Fjalëkalimet nuk përputhen');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Fjalëkalimi duhet të jetë së paku 6 karaktere');
      return;
    }

    setLoading(true);
    try {
      await register(formData.name, formData.email, formData.password);
      toast.success('Llogaria u krijua me sukses!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Regjistrimi dështoi');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Menaxhim i plotë financiar',
    'Raporte të automatizuara',
    'Njohuri me AI',
    'Ruajtje e sigurt në cloud'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#E9ECEF] to-[#DEE2E6] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <FinanciALLogo />
            <span className="text-2xl font-bold text-[#1A3C34]">FinanciAL</span>
          </Link>
          <h1 className="text-2xl font-bold text-[#1A3C34] mb-2">Krijo Llogari</h1>
          <p className="text-[#1A3C34]/60">Filloni udhëtimin tuaj financiar</p>
        </div>

        {/* Features */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {features.map((feature, i) => (
            <div key={i} className="flex items-center gap-1 text-xs text-[#1A3C34]/70 bg-white/50 px-3 py-1.5 rounded-full">
              <Check className="w-3 h-3 text-[#2A9D8F]" weight="bold" />
              {feature}
            </div>
          ))}
        </div>

        {/* Register Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#1A3C34]/5">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#1A3C34]">Emri i Plotë</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3C34]/40" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Emri Mbiemri"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 border-[#1A3C34]/10 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1A3C34]">Email</Label>
              <div className="relative">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3C34]/40" />
                <Input
                  id="email"
                  type="email"
                  placeholder="emri@shembull.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 border-[#1A3C34]/10 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1A3C34]">Fjalëkalimi</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3C34]/40" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 border-[#1A3C34]/10 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#1A3C34]">Konfirmo Fjalëkalimin</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3C34]/40" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="pl-10 border-[#1A3C34]/10 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white py-6 mt-2"
              disabled={loading}
            >
              {loading ? 'Duke krijuar...' : 'Krijo Llogari'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#1A3C34]/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-[#1A3C34]/50">ose</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full border-[#1A3C34]/10 hover:bg-[#F8F9FA] py-6"
            onClick={loginWithGoogle}
          >
            <GoogleLogo className="w-5 h-5 mr-2" weight="bold" />
            Vazhdo me Google
          </Button>
        </div>

        {/* Login link */}
        <p className="text-center mt-6 text-[#1A3C34]/60">
          Keni llogari?{' '}
          <Link to="/login" className="text-[#2A9D8F] font-medium hover:underline">
            Hyni
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Register;
